import { computed, inject } from '@angular/core';
import { StepType, WizardStep } from '@app/components/wizard/wizard.component';
import { AuthStore } from '@app/stores/auth.store';
import {
  patchState,
  signalStore,
  withComputed,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { WizardStore } from '../wizard.store';
import { StepSectionType } from './steps.enums';
import { getDefaultProjectWizardSteps } from './steps.helper';
import { StepsBySection } from './steps.models';

interface State {
  stepsBySectionTpye: StepsBySection;
  stepIndex: number;
}

export const StepsStore = signalStore(
  { providedIn: 'root' },
  withState<State>(() => ({
    stepsBySectionTpye: getDefaultProjectWizardSteps(),
    stepIndex: 0,
  })),
  withComputed((store) => {
    const orderedSections = computed(() => {
      const sections = Object.values(StepSectionType);
      return sections
        .filter((section) => typeof section === 'number')
        .filter(
          (section) => (section as StepSectionType) !== StepSectionType.admin,
        ) as StepSectionType[];
    });
    return {
      orderedSections: orderedSections,
      actualSteps: computed((): WizardStep<StepSectionType>[] => {
        return orderedSections()
          .map((section, index) => {
            const step = store.stepsBySectionTpye().get(section);
            if (step) {
              return { ...step, isActive: index === store.stepIndex() };
            }
            return undefined;
          })
          .filter((step): step is WizardStep<StepSectionType> => !!step);
      }),
    };
  }),
  withMethods((store) => {
    function setStepToActive(index: number) {
      patchState(store, {
        stepIndex: index,
      });
    }

    const updateStepStatus = (index: number, status: StepType) => {
      const sections = store.orderedSections();
      if (index >= 0 && index < sections.length) {
        const sectionType = sections[index];
        const currentMap = store.stepsBySectionTpye();
        const step = currentMap.get(sectionType);
        if (step) {
          const newMap = new Map(currentMap);
          newMap.set(sectionType, { ...step, stepType: status });
          patchState(store, { stepsBySectionTpye: newMap });
        }
      }
    };

    const batchUpdateStepStatus = (
      updates: { index: number; status: StepType }[],
    ) => {
      const sections = store.orderedSections();
      const currentMap = store.stepsBySectionTpye();
      const newMap = new Map(currentMap);
      let changed = false;
      updates.forEach(({ index, status }) => {
        if (index >= 0 && index < sections.length) {
          const sectionType = sections[index];
          const step = newMap.get(sectionType);
          if (step) {
            newMap.set(sectionType, { ...step, stepType: status });
            changed = true;
          }
        }
      });
      if (changed) {
        patchState(store, { stepsBySectionTpye: newMap });
      }
    };

    return {
      updateStep: (
        step: StepSectionType,
        newStep: WizardStep<StepSectionType>,
      ) => {
        store.stepsBySectionTpye().set(step, newStep);
        patchState(store, {
          stepsBySectionTpye: store.stepsBySectionTpye(),
        });
      },
      updateStepIndex: (newStepIndex: number) => {
        patchState(store, {
          stepIndex: newStepIndex,
        });
      },
      batchUpdateStepStatus,
      setStepToActive,
      markStepAsWarning: (index: number) =>
        updateStepStatus(index, StepType.hasWarrning),
      markStepAsDone: (index: number) => updateStepStatus(index, StepType.done),
      markStepAsAvailable: (index: number) =>
        updateStepStatus(index, StepType.available),
    };
  }),
  withHooks((store) => {
    const authStore = inject(AuthStore);
    const wizardStore = inject(WizardStore);

    return {
      onInit() {},
    };
  }),
);
